
import { useState } from 'react';
import { HelpCircle, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import type { TaskConfig, CapabilityMetadata, RetryRule, RetryPresetKey } from '../types';
import { RETRY_PRESETS, ERROR_TYPES } from '../types';

interface ResilienceTabProps {
    config: TaskConfig;
    capability: CapabilityMetadata | null;
    onTimeoutChange: (seconds: number | undefined) => void;
    onTimeoutPathChange: (path: string | undefined) => void;
    onHeartbeatChange: (seconds: number | undefined) => void;
    onAddRetry: () => void;
    onUpdateRetry: (id: string, updates: Partial<RetryRule>) => void;
    onRemoveRetry: (id: string) => void;
    onApplyPreset: (preset: RetryPresetKey) => void;
    readOnly: boolean;
}

export function ResilienceTab({
    config,
    capability,
    onTimeoutChange,
    onTimeoutPathChange,
    onHeartbeatChange,
    onAddRetry,
    onUpdateRetry,
    onRemoveRetry,
    onApplyPreset,
    readOnly,
}: ResilienceTabProps) {
    const [useDynamicTimeout, setUseDynamicTimeout] = useState(!!config.timeoutSecondsPath);
    const [expandedRules, setExpandedRules] = useState<Set<string>>(new Set());

    const toggleRuleExpanded = (id: string) => {
        setExpandedRules((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const isAsyncCapability = capability?.executionMode === 'job' || capability?.executionMode === 'callback';

    return (
        <div className="p-6 space-y-8">
            {/* Timeout Section */}
            <section className="space-y-4">
                <div className="flex items-center gap-2">
                    <Label className="text-base font-semibold">Timeout</Label>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger>
                                <HelpCircle size={14} className="text-slate-400" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                                <p>
                                    Maximum time allowed for this task to complete before timing out.
                                </p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>

                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={useDynamicTimeout}
                            onCheckedChange={(checked) => {
                                setUseDynamicTimeout(checked);
                                if (checked) {
                                    onTimeoutChange(undefined);
                                } else {
                                    onTimeoutPathChange(undefined);
                                }
                            }}
                            disabled={readOnly}
                        />
                        <Label className="text-sm">Dynamic timeout</Label>
                    </div>
                </div>

                {useDynamicTimeout ? (
                    <div className="space-y-2">
                        <Input
                            type="text"
                            value={config.timeoutSecondsPath || ''}
                            onChange={(e) => onTimeoutPathChange(e.target.value || undefined)}
                            placeholder="$.config.timeout"
                            className="font-mono"
                            disabled={readOnly}
                        />
                        <p className="text-xs text-slate-500">
                            JSONPath to a numeric value in the state (seconds).
                        </p>
                    </div>
                ) : (
                    <div className="flex items-center gap-2">
                        <Input
                            type="number"
                            value={config.timeoutSeconds ?? ''}
                            onChange={(e) => {
                                const val = e.target.value ? parseInt(e.target.value) : undefined;
                                onTimeoutChange(val);
                            }}
                            placeholder="30"
                            className="w-32"
                            min={0}
                            disabled={readOnly}
                        />
                        <span className="text-sm text-slate-500">seconds</span>
                    </div>
                )}
            </section>

            {/* Heartbeat Section - Only for async capabilities */}
            {isAsyncCapability && (
                <section className="space-y-4">
                    <div className="flex items-center gap-2">
                        <Label className="text-base font-semibold">Heartbeat</Label>
                        <Badge variant="outline" className="text-[10px]">
                            {capability?.executionMode}
                        </Badge>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger>
                                    <HelpCircle size={14} className="text-slate-400" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                    <p>
                                        For long-running tasks, how often to expect progress updates.
                                        If no heartbeat is received within this interval, the task times out.
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>

                    <div className="flex items-center gap-2">
                        <Input
                            type="number"
                            value={config.heartbeatSeconds ?? ''}
                            onChange={(e) => {
                                const val = e.target.value ? parseInt(e.target.value) : undefined;
                                onHeartbeatChange(val);
                            }}
                            placeholder="60"
                            className="w-32"
                            min={0}
                            disabled={readOnly}
                        />
                        <span className="text-sm text-slate-500">seconds between heartbeats</span>
                    </div>
                </section>
            )}

            {/* Retry Policy Section */}
            <section className="space-y-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Label className="text-base font-semibold">Retry Policy</Label>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger>
                                    <HelpCircle size={14} className="text-slate-400" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                    <p>
                                        Configure automatic retry behavior for transient failures.
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>

                    {!readOnly && (
                        <Select
                            value=""
                            onValueChange={(value) => onApplyPreset(value as RetryPresetKey)}
                        >
                            <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="Apply preset..." />
                            </SelectTrigger>
                            <SelectContent>
                                {Object.entries(RETRY_PRESETS).map(([key, preset]) => (
                                    <SelectItem key={key} value={key}>
                                        <div className="flex flex-col items-start">
                                            <span>{preset.label}</span>
                                            <span className="text-xs text-slate-400">
                                                {preset.description}
                                            </span>
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                </div>

                {/* Retry Rules List */}
                <div className="space-y-3">
                    {config.retry.map((rule) => {
                        const isExpanded = expandedRules.has(rule.id);
                        return (
                            <div
                                key={rule.id}
                                className="rounded-lg border border-slate-200 overflow-hidden"
                            >
                                {/* Rule Header */}
                                <div
                                    className="flex items-center justify-between p-3 bg-slate-50 cursor-pointer"
                                    onClick={() => toggleRuleExpanded(rule.id)}
                                >
                                    <div className="flex items-center gap-3">
                                        {isExpanded ? (
                                            <ChevronUp size={16} className="text-slate-400" />
                                        ) : (
                                            <ChevronDown size={16} className="text-slate-400" />
                                        )}
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-sm">
                                                    {rule.errorTypes.length === 1 && rule.errorTypes[0] === 'States.ALL'
                                                        ? 'All Errors'
                                                        : `${rule.errorTypes.length} error type(s)`}
                                                </span>
                                                <Badge variant="secondary" className="text-[10px]">
                                                    {rule.maxAttempts}x
                                                </Badge>
                                            </div>
                                            <p className="text-xs text-slate-500">
                                                {rule.initialDelay}s initial delay, {rule.backoffRate}x backoff
                                                {rule.jitter && ', with jitter'}
                                            </p>
                                        </div>
                                    </div>
                                    {!readOnly && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onRemoveRetry(rule.id);
                                            }}
                                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                        >
                                            <Trash2 size={14} />
                                        </Button>
                                    )}
                                </div>

                                {/* Expanded Content */}
                                {isExpanded && (
                                    <div className="p-4 space-y-4 border-t border-slate-200">
                                        {/* Error Types */}
                                        <div className="space-y-2">
                                            <Label className="text-sm">Error Types</Label>
                                            <div className="grid grid-cols-2 gap-2">
                                                {ERROR_TYPES.map((errorType) => (
                                                    <label
                                                        key={errorType.key}
                                                        className="flex items-center gap-2 text-sm"
                                                    >
                                                        <Checkbox
                                                            checked={rule.errorTypes.includes(errorType.key)}
                                                            onCheckedChange={(checked) => {
                                                                const newTypes = checked
                                                                    ? [...rule.errorTypes, errorType.key]
                                                                    : rule.errorTypes.filter((t) => t !== errorType.key);
                                                                onUpdateRetry(rule.id, {
                                                                    errorTypes: newTypes.length > 0 ? newTypes : ['States.ALL'],
                                                                });
                                                            }}
                                                            disabled={readOnly}
                                                        />
                                                        <span>{errorType.label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Retry Configuration */}
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label className="text-sm">Max Attempts</Label>
                                                <Input
                                                    type="number"
                                                    value={rule.maxAttempts}
                                                    onChange={(e) =>
                                                        onUpdateRetry(rule.id, {
                                                            maxAttempts: parseInt(e.target.value) || 1,
                                                        })
                                                    }
                                                    min={1}
                                                    max={99}
                                                    disabled={readOnly}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="text-sm">Initial Delay (s)</Label>
                                                <Input
                                                    type="number"
                                                    value={rule.initialDelay}
                                                    onChange={(e) =>
                                                        onUpdateRetry(rule.id, {
                                                            initialDelay: parseFloat(e.target.value) || 1,
                                                        })
                                                    }
                                                    min={0}
                                                    step={0.1}
                                                    disabled={readOnly}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="text-sm">Backoff Rate</Label>
                                                <Input
                                                    type="number"
                                                    value={rule.backoffRate}
                                                    onChange={(e) =>
                                                        onUpdateRetry(rule.id, {
                                                            backoffRate: parseFloat(e.target.value) || 1,
                                                        })
                                                    }
                                                    min={1}
                                                    step={0.1}
                                                    disabled={readOnly}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="text-sm">Max Delay (s)</Label>
                                                <Input
                                                    type="number"
                                                    value={rule.maxDelay ?? ''}
                                                    onChange={(e) =>
                                                        onUpdateRetry(rule.id, {
                                                            maxDelay: e.target.value ? parseFloat(e.target.value) : undefined,
                                                        })
                                                    }
                                                    placeholder="No limit"
                                                    min={0}
                                                    disabled={readOnly}
                                                />
                                            </div>
                                        </div>

                                        {/* Jitter Toggle */}
                                        <div className="flex items-center gap-2">
                                            <Switch
                                                checked={rule.jitter}
                                                onCheckedChange={(checked) =>
                                                    onUpdateRetry(rule.id, { jitter: checked })
                                                }
                                                disabled={readOnly}
                                            />
                                            <Label className="text-sm">Add jitter (randomization)</Label>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {config.retry.length === 0 && (
                        <div className="text-center py-6 text-slate-400 border border-dashed border-slate-200 rounded-lg">
                            No retry rules configured. Task will fail immediately on error.
                        </div>
                    )}

                    {!readOnly && (
                        <Button
                            variant="outline"
                            onClick={onAddRetry}
                            className="w-full"
                        >
                            <Plus size={16} className="mr-2" />
                            Add Retry Rule
                        </Button>
                    )}
                </div>
            </section>
        </div>
    );
}

